import { useTranslation } from 'react-i18next';
import {
  Button,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';

const TOOLTIP_DELAY_MS = 200;

/** Props for {@link AopMapViewToggle}. */
export interface IAopMapViewToggleProps {
  /** Switches between the document and the AOP map. */
  onToggle: () => void;
  /** Whether the map is showing, which points the toggle back at the document. */
  isMapView?: boolean;
}

/**
 * Switches an automation surface between its document and the AOP map.
 *
 * One button for both directions, labelled with where it goes rather than
 * where you are. Rendered wherever that surface keeps its document actions —
 * the page header, or the document's own title bar.
 */
export function AopMapViewToggle({
  onToggle,
  isMapView = false,
}: IAopMapViewToggleProps) {
  const { t } = useTranslation('automation');
  const label = t(
    isMapView
      ? 'automation.aopMap.tooltip.viewDocument'
      : 'automation.aopMap.tooltip.viewMap',
  );

  return (
    <Tooltip delayDuration={TOOLTIP_DELAY_MS} variant="primary">
      <TooltipTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          data-testid="toggle-aop-map-button"
          aria-label={label}
          onClick={onToggle}
        >
          <Icon type={isMapView ? 'FileText' : 'Map'} size="sm" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
